export const EMAIL_VERIFICATION_DOMAINS_ERROR = 'Enter valid email domains, e.g. luxuryescapes.com';

export const DIRECT_CLAIM_DOMAINS_REQUIRED_ERROR =
  'Direct campaigns without CS approval need at least one verified email domain. Without one, every Société member could claim this campaign.';

const DIRECT_CLAIM_DOMAINS_HELPER_TEXT =
  'Required for Direct campaigns without CS approval: only customers with a verified email at one of these domains can claim. Comma-separated.';

const EMAIL_VERIFICATION_DOMAINS_HELPER_TEXT =
  'Comma-separated email domains. Customers must have a verified email at one of these domains; leave empty for unrestricted. This can be updated after claims have been made.';

function isValidEmailDomain(domain: string): boolean {
  if (domain.length > 253) {
    return false;
  }

  const labels = domain.split('.');
  return labels.length > 1 && labels.every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label));
}

export function getEmailVerificationDomainsPayload(input: string): Array<string> {
  const normalizedDomains = input
    .split(',')
    .map((domain) => domain.trim().replace(/^@/, '').toLowerCase())
    .filter(Boolean);

  if (normalizedDomains.some((domain) => !isValidEmailDomain(domain))) {
    throw new Error(EMAIL_VERIFICATION_DOMAINS_ERROR);
  }

  return [...new Set(normalizedDomains)];
}

export function formatEmailVerificationDomains(domains?: Array<string>): string {
  return domains?.join(', ') ?? '';
}

export function validateEmailVerificationDomainsInput(input: string): true | string {
  try {
    getEmailVerificationDomainsPayload(input);
    return true;
  } catch {
    return EMAIL_VERIFICATION_DOMAINS_ERROR;
  }
}

export interface ClaimCampaignGrantSettings {
  validatorType: string;
  campaignType?: string;
  requireApproval?: boolean;
}

/**
 * A Direct NORMAL campaign without CS approval grants the reward to anyone who claims, with no code and
 * no review, so the email domain allowlist is the only thing limiting who can claim. MATCH (e.g. Status
 * Match) and CS approval campaigns are reviewed before anything is granted. Mirrors svc-lux-loyalty.
 */
export function requiresEmailVerificationDomains({
  validatorType,
  campaignType,
  requireApproval,
}: ClaimCampaignGrantSettings): boolean {
  return validatorType === 'DIRECT' && campaignType !== 'MATCH' && !requireApproval;
}

export function validateEmailVerificationDomainsForCampaign(
  input: string,
  settings: ClaimCampaignGrantSettings,
): true | string {
  const formatResult = validateEmailVerificationDomainsInput(input);
  if (formatResult !== true) return formatResult;
  if (requiresEmailVerificationDomains(settings) && getEmailVerificationDomainsPayload(input).length === 0) {
    return DIRECT_CLAIM_DOMAINS_REQUIRED_ERROR;
  }
  return true;
}

export function getEmailVerificationDomainsHelperText(
  isFocused: boolean,
  errorMessage?: string,
  isRequired?: boolean,
): string | undefined {
  if (errorMessage) return errorMessage;
  if (isRequired) return DIRECT_CLAIM_DOMAINS_HELPER_TEXT;
  return isFocused ? EMAIL_VERIFICATION_DOMAINS_HELPER_TEXT : undefined;
}
